import React, { useState } from "react";

interface Pocket {
    id: string;
    name: string;
    balance: number;
    currency: string;
    type: 'main' | 'savings' | 'investment' | 'escrow';
    status: 'active' | 'frozen' | 'closed';
    lastTransaction?: string;
}

interface BillingWalletProps {
    accountBalance: number;
    userEmail: string;
    pockets: Pocket[];
}

const BillingWallet: React.FC<BillingWalletProps> = ({ accountBalance, userEmail, pockets }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDescription, setPaymentDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const handlePayment = async () => {
        if (!paymentAmount || !paymentDescription) return;
        
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/billing/api/wallet-payment/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: userEmail,
                    amount: parseFloat(paymentAmount),
                    description: paymentDescription
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setShowPaymentModal(false);
                setPaymentAmount('');
                setPaymentDescription('');
                alert('Payment successful!');
            } else {
                alert('Payment failed: ' + data.message);
            }
        } catch (error) {
            alert('Payment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'KES') => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const getPocketIcon = (type: string) => {
        switch (type) {
            case 'main': return 'ri-wallet-3-line text-primary';
            case 'savings': return 'ri-safe-2-line text-success';
            case 'investment': return 'ri-line-chart-line text-warning';
            case 'escrow': return 'ri-shield-check-line text-info';
            default: return 'ri-wallet-line text-gray-500';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'frozen': return 'warning';
            case 'closed': return 'danger';
            default: return 'secondary';
        }
    };

    const renderOverview = () => (
        <div className="space-y-8">
            <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white bg-opacity-10 rounded-full translate-y-16 -translate-x-16"></div>
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-bold mb-2">Total Balance</h2>
                            <p className="text-purple-100">Available for transactions</p>
                        </div>
                        <div className="p-4 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl">
                            <i className="ri-wallet-3-line text-3xl"></i>
                        </div>
                    </div>
                    <div className="text-4xl font-bold mb-4">{formatCurrency(accountBalance)}</div>
                    <div className="flex items-center space-x-6">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                            <span className="text-sm text-purple-100">Wallet Active</span>
                        </div>
                        <div className="flex items-center">
                            <i className="ri-shield-check-line mr-2 text-purple-200"></i>
                            <span className="text-sm text-purple-100">Secure & Verified</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <button
                    onClick={() => setShowPaymentModal(true)}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group hover:scale-105"
                >
                    <div className="flex items-center">
                        <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl mr-4 group-hover:from-green-600 group-hover:to-emerald-600 transition-all duration-300">
                            <i className="ri-send-plane-line text-white text-2xl"></i>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg mb-1">Send Payment</h3>
                            <p className="text-sm text-gray-600">Make a payment</p>
                        </div>
                    </div>
                </button>

                <button className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group hover:scale-105">
                    <div className="flex items-center">
                        <div className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl mr-4 group-hover:from-blue-600 group-hover:to-indigo-600 transition-all duration-300">
                            <i className="ri-exchange-line text-white text-2xl"></i>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg mb-1">Transfer</h3>
                            <p className="text-sm text-gray-600">Between pockets</p>
                        </div>
                    </div>
                </button>

                <button className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group hover:scale-105">
                    <div className="flex items-center">
                        <div className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mr-4 group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300">
                            <i className="ri-download-line text-white text-2xl"></i>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg mb-1">Withdraw</h3>
                            <p className="text-sm text-gray-600">To bank account</p>
                        </div>
                    </div>
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Your Pockets</h3>
                    <button className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                        Manage Pockets
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {pockets.map((pocket) => (
                        <div key={pocket.id} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-gray-300 group">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <div className={`p-3 rounded-xl mr-3 ${
                                        pocket.type === 'main' ? 'bg-blue-100' :
                                        pocket.type === 'savings' ? 'bg-green-100' :
                                        pocket.type === 'investment' ? 'bg-yellow-100' :
                                        'bg-purple-100'
                                    }`}>
                                        <i className={`ri ${getPocketIcon(pocket.type)} text-xl ${
                                            pocket.type === 'main' ? 'text-blue-600' :
                                            pocket.type === 'savings' ? 'text-green-600' :
                                            pocket.type === 'investment' ? 'text-yellow-600' :
                                            'text-purple-600'
                                        }`}></i>
                                    </div>
                                    <span className="font-semibold text-gray-900">{pocket.name}</span>
                                </div>
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                    pocket.status === 'active' ? 'bg-green-100 text-green-800' :
                                    pocket.status === 'frozen' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                }`}>
                                    {pocket.status}
                                </span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900 mb-2">
                                {formatCurrency(pocket.balance, pocket.currency)}
                            </p>
                            <p className="text-sm text-gray-600 capitalize">{pocket.type} pocket</p>
                            {pocket.lastTransaction && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Last: {new Date(pocket.lastTransaction).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-8">
                        {[
                            { id: 'overview', label: 'Overview', icon: '📊' },
                            { id: 'transactions', label: 'Transactions', icon: '💳' },
                            { id: 'settings', label: 'Settings', icon: '⚙️' }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-6 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                                    activeTab === tab.id
                                        ? 'border-purple-500 text-purple-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <span className="mr-2 text-lg">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'transactions' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Transactions</h3>
                        <div className="text-center py-12 text-gray-500">
                            <i className="ri-exchange-line text-4xl mb-4"></i>
                            <p>Transaction history will be displayed here.</p>
                        </div>
                    </div>
                )}
                {activeTab === 'settings' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Wallet Settings</h3>
                        <div className="text-center py-12 text-gray-500">
                            <i className="ri-settings-3-line text-4xl mb-4"></i>
                            <p>Wallet settings and preferences will be available here.</p>
                        </div>
                    </div>
                )}
            </div>

            {showPaymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Send Payment</h3>
                            <button
                                onClick={() => setShowPaymentModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <i className="ri-close-line text-2xl"></i>
                            </button>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">Amount</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <i className="ri-money-dollar-circle-line text-gray-400"></i>
                                    </div>
                                    <input
                                        type="number"
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                        placeholder="Enter amount"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">Description</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <i className="ri-file-text-line text-gray-400"></i>
                                    </div>
                                    <input
                                        type="text"
                                        value={paymentDescription}
                                        onChange={(e) => setPaymentDescription(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                        placeholder="Payment description"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex space-x-4 mt-8">
                            <button
                                onClick={() => setShowPaymentModal(false)}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePayment}
                                disabled={loading || !paymentAmount || !paymentDescription}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        Processing...
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center">
                                        <i className="ri-send-plane-line mr-2"></i>
                                        Send Payment
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingWallet;
